import { ReactNode, useMemo, useState } from 'react'
import { Divider } from 'semantic-ui-react'
import { Truncate } from '@re-dev/react-truncate'
import { faCircle as fatCircle } from '@fortawesome/pro-thin-svg-icons'
import {
  faTrashCan,
  faSquareArrowUpRight,
  faUpload,
} from '@fortawesome/pro-regular-svg-icons'
import {
  faXmark,
  faCheck,
  faCircle as fasCircle,
} from '@fortawesome/pro-solid-svg-icons'
import {
  Button,
  Card,
  Checkbox,
  Icon,
  IconButton,
  Link,
  Popup,
  Text,
} from '../../BaseComponents'
import { StackedIcon } from '../../BaseComponents/Icon'
import { UserDocument } from '@/features/user/your-practice/documents/lib/userDocuments.slice'
import {
  Document,
  Receipt,
} from '@/features/bookkeeping/end-of-year-review/types'
import styles from './styles.module.scss'
import type { Colors } from '../../../styles/theme'
import { useShowDocumentErrors } from '@/features/bookkeeping/components/document-validation'
import DeleteDocumentModal from '@/features/taxes/annual-taxes/tax-checklist/documents/DeleteDocumentModal'

type PartialDoc = Pick<UserDocument, 'id' | 'fileDescription' | 'signedUrl'>

export interface IDocumentUploadCardProps {
  name: string
  bookkeeperNote?: string
  description: ReactNode
  explanation?: ReactNode
  documents?: PartialDoc[]
  handleModalToggle?: () => void
  handleUploadBtn?: () => void
  hasFileUploadError?: boolean
  href?: string
  linkText?: string
  requiredDocCount?: number
  setChecked?: () => void
  notRelevant?: boolean
  status?: 'none' | 'disabled' | 'success'
  cardBackgroundColor?: keyof typeof Colors
  readOnly?: boolean
  hideNoUploadIndicator?: boolean
  rightContent?: ReactNode
  isRequiredReviewed?: boolean | Document | Receipt
  isFollowUp?: boolean
  onDocumentDeleted?: (documentId: number) => void
  hideValidationError?: boolean
}
const icons = {
  disabled: {
    icon: faXmark,
    backgroundColor: 'gray',
  },
  success: {
    icon: faCheck,
    backgroundColor: 'green',
  },
} as const

const bgColor = {
  none: 'natural',
  disabled: 'stone40',
  success: 'lightGreen',
  error: 'lightRed',
} as const

const UploadedDocumentLine = ({
  id,
  fileDescription,
  signedUrl,
  readOnly,
  onDocumentDeleted,
}: {
  readOnly?: boolean
  onDocumentDeleted?: (documentId: number) => void
} & PartialDoc) => {
  const [openModal, setOpenModal] = useState(false)

  const fileExtension = fileDescription?.includes('.')
    ? fileDescription.substring(fileDescription.lastIndexOf('.'))
    : ''

  const content = useMemo(
    () => (
      <Popup
        content={`File Name: ${fileDescription}`}
        trigger={
          <span>
            <Truncate width={200} ellipsis={`...${fileExtension}`}>
              {fileDescription || ''}
            </Truncate>
          </span>
        }
      />
    ),
    [fileDescription, fileExtension]
  )

  return (
    <div className={styles.document}>
      {!signedUrl && <Text className={styles.docName}>{content}</Text>}
      {signedUrl && (
        <Link href={signedUrl} className={styles.docName}>
          {content}
        </Link>
      )}
      {!readOnly && (
        <IconButton
          onClick={() => setOpenModal(true)}
          icon={faTrashCan}
          size="lg"
        />
      )}
      <DeleteDocumentModal
        open={openModal}
        close={() => setOpenModal(false)}
        documentId={id}
        onDeleted={() => onDocumentDeleted?.(id)}
      />
    </div>
  )
}

export const DocumentUploadCard = ({
  name,
  description,
  explanation,
  bookkeeperNote,
  documents = [],
  handleUploadBtn,
  hasFileUploadError = false,
  href,
  linkText = 'View instructions',
  requiredDocCount = 0,
  notRelevant = false,
  setChecked,
  status = 'none',
  isRequiredReviewed,
  cardBackgroundColor,
  readOnly = false,
  hideNoUploadIndicator = false,
  rightContent,
  isFollowUp = false,
  onDocumentDeleted,
  hideValidationError = false,
}: IDocumentUploadCardProps) => {
  const showDocumentErrors = useShowDocumentErrors()
  const hasValidationError =
    showDocumentErrors &&
    !hideValidationError &&
    !readOnly &&
    status === 'none' &&
    !notRelevant &&
    Boolean(handleUploadBtn)

  if (
    isFollowUp &&
    typeof isRequiredReviewed === 'object' &&
    !isRequiredReviewed?.requestReUpload &&
    !isRequiredReviewed.updatedByUser
  ) {
    return null
  }
  return (
    <Card
      backgroundColor={
        typeof isRequiredReviewed === 'boolean' || !isRequiredReviewed
          ? cardBackgroundColor ||
            bgColor[hasValidationError ? 'error' : status]
          : typeof isRequiredReviewed === 'object' &&
              (!isRequiredReviewed?.requestReUpload ||
                isRequiredReviewed.updatedByUser)
            ? cardBackgroundColor || bgColor[status]
            : bgColor['error']
      }
    >
      <div className={styles.card}>
        <div>
          {typeof isRequiredReviewed !== 'object' || !isRequiredReviewed ? (
            status === 'none' ? (
              <Icon
                color="gray"
                icon={fatCircle}
                size="3x"
                title={`${status}-status-icon`}
              />
            ) : (
              <StackedIcon
                title={`${status}-status-icon`}
                color="white"
                size="1x"
                backgroundIcon={fasCircle}
                backgroundSize={'3x'}
                {...icons[status]}
              />
            )
          ) : !isRequiredReviewed?.requestReUpload ||
            isRequiredReviewed.updatedByUser ? (
            <StackedIcon
              title={`${status}-status-icon`}
              color="white"
              size="1x"
              backgroundIcon={fasCircle}
              backgroundSize={'3x'}
              {...icons['success']}
            />
          ) : (
            <StackedIcon
              title={`${status}-status-icon`}
              color="white"
              size="1x"
              backgroundIcon={fasCircle}
              backgroundSize={'3x'}
              {...icons['disabled']}
            />
          )}
        </div>
        <div className={styles.centerColumn}>
          <div className={styles.details}>
            <Text as="h3">{name}</Text>
            <Text as="bodySm">{description}</Text>
            {explanation && <Text as="bodySm">{explanation}</Text>}
            {href && (
              <div>
                <Link href={href} newPage size="small">
                  {linkText}
                </Link>
                <Icon
                  icon={faSquareArrowUpRight}
                  className={styles.instructionsIcon}
                />
              </div>
            )}
          </div>

          {hasValidationError && (
            <Text as="bodySm" color="red" className={styles.validationError}>
              <strong>
                {setChecked
                  ? 'Upload this document or mark it Not relevant.'
                  : 'Upload this document.'}
              </strong>
            </Text>
          )}

          {bookkeeperNote && (
            <>
              <Text as="h3" color="red" className={styles.reuploadDoc}>
                <strong>Please reupload this document.</strong>
              </Text>

              <Text color="red">Bookkeeper Note: {bookkeeperNote}</Text>
            </>
          )}

          {documents.length > 0 && (
            <>
              <Divider />

              {documents?.map((doc) => (
                <UploadedDocumentLine
                  key={doc.id}
                  {...doc}
                  readOnly={readOnly}
                  onDocumentDeleted={onDocumentDeleted}
                />
              ))}

              {requiredDocCount > 1 && (
                <Text className={styles.docCount}>
                  Uploaded {documents.length} of {requiredDocCount} documents
                </Text>
              )}
            </>
          )}
          {readOnly && documents.length === 0 && !hideNoUploadIndicator && (
            <>
              <Divider />
              <Text color="darkGray">
                <i>No Uploads</i>
              </Text>
            </>
          )}
        </div>
        <div className={styles.rightColumn}>
          {!readOnly && (
            <>
              <Button
                className={styles.uploadBtn}
                variant="secondary"
                size="small"
                fullWidth
                onClick={handleUploadBtn}
                disabled={
                  typeof isRequiredReviewed === 'object'
                    ? !isRequiredReviewed?.requestReUpload
                    : requiredDocCount > 0 &&
                      documents.length >= requiredDocCount
                }
              >
                <Icon icon={faUpload} size="lg" className={styles.uploadIcon} />
                Upload
              </Button>

              {hasFileUploadError && (
                <Text color="red" className={styles.error}>
                  <strong>Error: </strong>Your document couldn’t be uploaded.
                  Please try again later.
                </Text>
              )}

              {documents?.length === 0 && setChecked && (
                <Popup
                  hoverable
                  position="bottom right"
                  content={
                    <div className={styles.popup}>
                      <Text as="h3" className={styles.header}>
                        <strong>Not relevant</strong>
                      </Text>

                      <Text className={styles.topText}>
                        Check this box only if you’re sure this document doesn’t
                        apply to you.
                      </Text>

                      <Text className={styles.bottomText}>
                        <strong>We recommend double-checking first</strong>
                      </Text>

                      <Text>
                        This could delay your end-of-year bookkeeping and annual
                        tax filing if we find we still need the document.
                      </Text>
                    </div>
                  }
                  trigger={
                    <div className={styles.notRelevant}>
                      <Checkbox
                        id={name}
                        label="Not relevant"
                        labelStyle={{ fontSize: 14, lineHeight: '28px' }}
                        onChange={() => setChecked?.()}
                        checked={notRelevant}
                        variant="default"
                      />
                    </div>
                  }
                />
              )}
            </>
          )}
          {rightContent}
        </div>
      </div>
      {typeof isRequiredReviewed === 'object' &&
        isRequiredReviewed?.requestReUpload &&
        !isRequiredReviewed.updatedByUser && (
          <>
            <Divider />
            <Text color="red" className={styles.success}>
              <strong>Please reupload this document.</strong>
            </Text>
            <Text color="red" className={styles.success}>
              Bookkeeper Note: {isRequiredReviewed.adminNote}
            </Text>
          </>
        )}
    </Card>
  )
}
